import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Save, Download, FileDown, CheckCircle2 } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const emptyProject = {
  quote_no: "", customer: "", location: "", scope: "",
  job_type: 3, currency: "TRY", vat_percent: 20, duration_days: 30,
  shift: "Tek", delivery_terms: "", payment_terms: "%50 avans / %50 teslimde",
  validity_days: 30,
  personnel: [], daily_expenses: [], materials: [], equipment: [], material_sales: [],
  overhead_percent: 10, contingency_percent: 5, risk_percent: 5,
  profit_percent: 25, discount_percent: 0, pricing_method: "A", target_margin: 25,
  status: "draft", exclusions: "", notes: "", show_material_detail: false,
};

const JOB_TYPES = { 1: "Sadece Malzeme Satışı", 2: "Sadece İşçilik/Uygulama", 3: "Malzeme + İşçilik (Karma)" };

export default function ProjectWizard() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(emptyProject);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(!!id);

  useEffect(() => {
    (async () => {
      const [pr, su] = await Promise.all([api.get("/products"), api.get("/suppliers")]);
      setProducts(pr.data); setSuppliers(su.data);
      if (id) {
        const r = await api.get(`/projects/${id}`);
        setP({ ...emptyProject, ...r.data });
      }
    })();
  }, [id]);

  // Live summary
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/projects/preview-summary", p);
        setSummary(data);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [p]);

  const setField = (k, v) => setP((old) => ({ ...old, [k]: v }));

  const addRow = (key, template) => setP((old) => ({ ...old, [key]: [...(old[key] || []), template] }));
  const updRow = (key, idx, field, value) => setP((old) => {
    const arr = [...(old[key] || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    return { ...old, [key]: arr };
  });
  const delRow = (key, idx) => setP((old) => ({ ...old, [key]: old[key].filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      if (id) {
        await api.put(`/projects/${id}`, p);
        toast.success("Kaydedildi");
      } else {
        const { data } = await api.post("/projects", p);
        toast.success(`Oluşturuldu: ${data.quote_no}`);
        setSaved(true);
        nav(`/projects/${data.id}`);
      }
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally { setSaving(false); }
  };

  const download = async (kind) => {
    if (!id) { toast.error("Önce kaydedin"); return; }
    const url = kind === "xlsx" ? `/projects/${id}/export/xlsx` : `/projects/${id}/customer-offer.pdf`;
    try {
      const res = await api.get(url, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `teklif.${kind}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([res.data]));
      a.download = filename; a.click();
      toast.success(kind === "xlsx" ? "Excel indirildi (14 sayfa formüllü)" : "PDF indirildi");
    } catch { toast.error("İndirme başarısız"); }
  };

  const approve = async () => {
    if (!id) return;
    try {
      await api.post(`/projects/${id}/approve`);
      toast.success("Onaylandı");
      const r = await api.get(`/projects/${id}`);
      setP({ ...emptyProject, ...r.data });
    } catch (e) { toast.error("Onay yetkiniz yok"); }
  };

  const jt = Number(p.job_type);
  const showLabor = jt === 2 || jt === 3;
  const showMaterial = jt === 1 || jt === 3;

  return (
    <div className="space-y-4" data-testid="project-wizard">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Teklif</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight mt-1">
            {id ? `${p.quote_no || "Teklif"} — ${p.customer || "Müşteri"}` : "Yeni Teklif"}
          </h2>
        </div>
        <div className="flex gap-2">
          <Button data-testid="save-btn" onClick={save} disabled={saving} className="bg-primary hover:bg-orange-600 text-white">
            <Save size={16} className="mr-2" /> {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
          {id && (
            <>
              <Button data-testid="export-xlsx-btn" variant="outline" onClick={() => download("xlsx")}>
                <Download size={16} className="mr-2" /> Excel (14 sayfa)
              </Button>
              <Button data-testid="export-pdf-btn" variant="outline" onClick={() => download("pdf")}>
                <FileDown size={16} className="mr-2" /> PDF
              </Button>
              {p.status !== "approved" && (
                <Button data-testid="approve-btn" variant="outline" onClick={approve} className="text-green-700 border-green-600 hover:bg-green-50">
                  <CheckCircle2 size={16} className="mr-2" /> Onayla
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* LEFT: WIZARD */}
        <div className="xl:col-span-2 space-y-4">
          <Card className="p-5 border-border">
            <h3 className="font-heading text-lg font-semibold mb-4">1. Proje Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Teklif No"><Input data-testid="quote-no" value={p.quote_no} onChange={(e) => setField("quote_no", e.target.value)} placeholder="TKF-2026-0001" /></Field>
              <Field label="Müşteri"><Input data-testid="customer" value={p.customer} onChange={(e) => setField("customer", e.target.value)} /></Field>
              <Field label="Lokasyon"><Input data-testid="location" value={p.location} onChange={(e) => setField("location", e.target.value)} /></Field>
              <Field label="İş Türü">
                <Select value={String(p.job_type)} onValueChange={(v) => setField("job_type", Number(v))}>
                  <SelectTrigger data-testid="job-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(JOB_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Kapsam" full><Textarea data-testid="scope" value={p.scope} onChange={(e) => setField("scope", e.target.value)} rows={2} /></Field>
              <Field label="Süre (gün)"><Input type="number" value={p.duration_days} onChange={(e) => setField("duration_days", Number(e.target.value))} /></Field>
              <Field label="KDV %"><Input type="number" value={p.vat_percent} onChange={(e) => setField("vat_percent", Number(e.target.value))} /></Field>
              <Field label="Para Birimi">
                <Select value={p.currency} onValueChange={(v) => setField("currency", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRY">TRY</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Ödeme Şartları"><Input value={p.payment_terms} onChange={(e) => setField("payment_terms", e.target.value)} /></Field>
              <Field label="Teslim Şartları"><Input value={p.delivery_terms} onChange={(e) => setField("delivery_terms", e.target.value)} /></Field>
              <Field label="Geçerlilik (gün)"><Input type="number" value={p.validity_days} onChange={(e) => setField("validity_days", Number(e.target.value))} /></Field>
            </div>
          </Card>

          <Tabs defaultValue={showLabor ? "personnel" : "materials"}>
            <TabsList className="w-full grid grid-cols-2 md:grid-cols-5 h-auto">
              {showLabor && <TabsTrigger value="personnel" data-testid="tab-personnel">Personel</TabsTrigger>}
              {showLabor && <TabsTrigger value="daily" data-testid="tab-daily">Saha Gid.</TabsTrigger>}
              {showLabor && <TabsTrigger value="materials-app" data-testid="tab-materials-app">Malz. (Uyg.)</TabsTrigger>}
              {showLabor && <TabsTrigger value="equipment" data-testid="tab-equipment">Ekipman</TabsTrigger>}
              {showMaterial && <TabsTrigger value="material-sales" data-testid="tab-material-sales">Malz. Satış</TabsTrigger>}
            </TabsList>

            {showLabor && <TabsContent value="personnel">
              <RowTable
                title="Personel" testid="personnel-table"
                cols={[
                  { k: "position", label: "Pozisyon", type: "text" },
                  { k: "people", label: "Kişi", type: "number" },
                  { k: "days", label: "Gün", type: "number" },
                  { k: "daily_wage", label: "Günlük Ücret", type: "number" },
                  { k: "overtime_hours", label: "Mesai Saat", type: "number" },
                ]}
                rows={p.personnel}
                onAdd={() => addRow("personnel", { position: "Refrakter Ustası", people: 1, days: 10, daily_wage: 2000, overtime_hours: 0 })}
                onUpd={(i, f, v) => updRow("personnel", i, f, v)}
                onDel={(i) => delRow("personnel", i)}
                totalLine={<>Personel toplam: <b className="text-primary num">{fmt(summary?.personnel?.total)}</b></>}
              />
            </TabsContent>}

            {showLabor && <TabsContent value="daily">
              <RowTable
                title="Saha ve Günlük Giderler" testid="daily-table"
                cols={[
                  { k: "name", label: "Kalem", type: "text" },
                  { k: "people", label: "Kişi", type: "number" },
                  { k: "days", label: "Gün", type: "number" },
                  { k: "unit_price", label: "Birim Fiyat", type: "number" },
                ]}
                rows={p.daily_expenses}
                onAdd={() => addRow("daily_expenses", { name: "Yemek", people: 1, days: 10, unit_price: 150 })}
                onUpd={(i, f, v) => updRow("daily_expenses", i, f, v)}
                onDel={(i) => delRow("daily_expenses", i)}
                totalLine={<>Toplam: <b className="text-primary num">{fmt(summary?.daily_expenses?.total)}</b></>}
              />
            </TabsContent>}

            {showLabor && <TabsContent value="materials-app">
              <RowTable
                title="Malzeme (Uygulama için tüketim)" testid="materials-table"
                cols={[
                  { k: "name", label: "Ürün", type: "text" },
                  { k: "unit", label: "Birim", type: "text" },
                  { k: "quantity", label: "Miktar", type: "number" },
                  { k: "unit_price", label: "Birim Fiyat", type: "number" },
                  { k: "waste_percent", label: "Fire %", type: "number" },
                  { k: "freight", label: "Nakliye", type: "number" },
                ]}
                rows={p.materials}
                onAdd={() => addRow("materials", { name: "", unit: "adet", quantity: 100, unit_price: 50, waste_percent: 3, freight: 0 })}
                onUpd={(i, f, v) => updRow("materials", i, f, v)}
                onDel={(i) => delRow("materials", i)}
                totalLine={<>Malzeme toplam: <b className="text-primary num">{fmt(summary?.materials?.total)}</b></>}
              />
            </TabsContent>}

            {showLabor && <TabsContent value="equipment">
              <RowTable
                title="Ekipman ve Dış Hizmet" testid="equipment-table"
                cols={[
                  { k: "name", label: "Kalem", type: "text" },
                  { k: "quantity", label: "Miktar", type: "number" },
                  { k: "days", label: "Gün", type: "number" },
                  { k: "unit_price", label: "Birim Fiyat", type: "number" },
                ]}
                rows={p.equipment}
                onAdd={() => addRow("equipment", { name: "İskele", quantity: 1, days: 10, unit_price: 500 })}
                onUpd={(i, f, v) => updRow("equipment", i, f, v)}
                onDel={(i) => delRow("equipment", i)}
                totalLine={<>Ekipman toplam: <b className="text-primary num">{fmt(summary?.equipment?.total)}</b></>}
              />
            </TabsContent>}

            {showMaterial && <TabsContent value="material-sales">
              <MaterialSalesTable
                rows={p.material_sales}
                products={products}
                suppliers={suppliers}
                onAdd={(prod) => addRow("material_sales", {
                  product_code: prod?.code || "",
                  product_name: prod?.name || "",
                  quality_class: prod?.quality_class || "",
                  origin: prod?.origin || "Yurt İçi",
                  unit: prod?.unit || "adet",
                  supplier_name: "",
                  quantity: 100, purchase_unit_price: 0, purchase_currency: "TRY",
                  extras: { customs: 0, freight_intl: 0, freight_dom: 0, insurance: 0, bank: 0, packaging: 0, storage: 0, commission: 0, finance: 0, other: 0 },
                  waste_percent: 3, profit_method: "A", profit_percent: 25, target_margin_percent: 25, discount_percent: 0,
                })}
                onUpd={(i, f, v) => updRow("material_sales", i, f, v)}
                onDel={(i) => delRow("material_sales", i)}
                summary={summary?.material_sales}
              />
            </TabsContent>}
          </Tabs>

          {showLabor && (
            <Card className="p-5 border-border">
              <h3 className="font-heading text-lg font-semibold mb-4">Fiyatlandırma (Uygulama)</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Genel Gider %"><Input type="number" value={p.overhead_percent} onChange={(e) => setField("overhead_percent", Number(e.target.value))} /></Field>
                <Field label="Beklenmeyen %"><Input type="number" value={p.contingency_percent} onChange={(e) => setField("contingency_percent", Number(e.target.value))} /></Field>
                <Field label="Risk %"><Input type="number" value={p.risk_percent} onChange={(e) => setField("risk_percent", Number(e.target.value))} /></Field>
                <Field label="Yöntem">
                  <Select value={p.pricing_method} onValueChange={(v) => setField("pricing_method", v)}>
                    <SelectTrigger data-testid="pricing-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">A: Maliyet + Kâr%</SelectItem>
                      <SelectItem value="B">B: Hedef Marj</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                {p.pricing_method === "A" ? (
                  <Field label="Kâr %"><Input type="number" value={p.profit_percent} onChange={(e) => setField("profit_percent", Number(e.target.value))} /></Field>
                ) : (
                  <Field label="Hedef Marj %"><Input type="number" value={p.target_margin} onChange={(e) => setField("target_margin", Number(e.target.value))} /></Field>
                )}
                <Field label="İskonto %"><Input type="number" value={p.discount_percent} onChange={(e) => setField("discount_percent", Number(e.target.value))} /></Field>
              </div>
            </Card>
          )}

          <Card className="p-5 border-border">
            <h3 className="font-heading text-lg font-semibold mb-4">Kapsam Dışı / Notlar</h3>
            <Textarea rows={3} placeholder="Kapsam dışı hususlar (isteğe bağlı)" value={p.exclusions} onChange={(e) => setField("exclusions", e.target.value)} />
            {(jt === 1 || jt === 3) && (
              <label className="mt-4 flex items-start gap-2 cursor-pointer select-none" data-testid="show-material-detail-wrap">
                <input
                  type="checkbox"
                  data-testid="show-material-detail"
                  checked={!!p.show_material_detail}
                  onChange={(e) => setField("show_material_detail", e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-primary cursor-pointer"
                />
                <span className="text-sm">
                  <span className="font-medium">Müşteri PDF'inde malzeme detayını göster</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    {jt === 1
                      ? "Sadece malzeme teklifinde bu tablo daima gösterilir."
                      : "Karma teklifte uygulama özetinin altına malzeme detay tablosu eklenir (kalem kalem, para birimine göre gruplu)."}
                  </span>
                </span>
              </label>
            )}
          </Card>
        </div>

        {/* RIGHT: LIVE SUMMARY */}
        <div className="xl:col-span-1">
          <Card className="p-5 border-border sticky top-24" data-testid="live-summary">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Canlı Özet</div>
            <h3 className="font-heading text-xl font-bold mt-1">Maliyet & Kâr</h3>
            <div className="mt-4 space-y-3 text-sm">
              {showLabor && summary?.application && (
                <SummarySection title="Uygulama">
                  <Row label="Direkt" value={summary.application.direct} />
                  <Row label={`SGK ${summary?.personnel?.sgk_method === 'daily_fixed' ? '(Günlük Sabit)' : '(Yüzde)'}`} value={summary?.personnel?.sgk_amount} />
                  <Row label="Genel Gider" value={summary.application.overhead} />
                  <Row label="Beklenmeyen" value={summary.application.contingency} />
                  <Row label="Risk" value={summary.application.risk} />
                  <Row label="Toplam Maliyet" value={summary.application.total_cost} bold />
                  <Row label="Satış (KDV Hariç)" value={summary.application.sale_price} bold accent />
                  <Row label={`Brüt Kâr / Marj`} value={`${fmt(summary.application.profit)} / %${fmt(summary.application.margin)}`} isText />
                </SummarySection>
              )}
              {showMaterial && summary?.material_sales_summary?.by_currency && Object.keys(summary.material_sales_summary.by_currency).length > 0 && (
                <SummarySection title="Malzeme Satış (para birimine göre)">
                  {Object.entries(summary.material_sales_summary.by_currency).map(([cur, tot]) => (
                    <div key={cur} className="border-l-2 border-primary/30 pl-2 mb-2">
                      <div className="text-[10px] uppercase tracking-widest text-primary font-bold">{cur}</div>
                      <Row label="Alış Maliyet" value={tot.cost} currency={cur} />
                      <Row label="Satış (KDV Hariç)" value={tot.sale} currency={cur} bold accent />
                      <Row label={`KDV %${p.vat_percent}`} value={tot.vat} currency={cur} />
                      <Row label="KDV Dahil" value={tot.sale_with_vat} currency={cur} bold />
                      <Row label="Kâr / Marj" value={`${fmt(tot.profit)} ${cur} / %${fmt(tot.margin)}`} isText />
                    </div>
                  ))}
                </SummarySection>
              )}
              {summary?.grand?.by_currency && Object.keys(summary.grand.by_currency).length > 0 && (
                <div className="pt-3 border-t border-border">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">GENEL TOPLAM (para birimine göre ayrı)</div>
                  {Object.entries(summary.grand.by_currency).map(([cur, tot]) => (
                    <div key={cur} className="mb-2">
                      <Row label={`Toplam (KDV Hariç)`} value={tot.sale} currency={cur} bold accent big />
                      <Row label={`KDV %${p.vat_percent}`} value={tot.vat} currency={cur} />
                      <Row label="KDV Dahil" value={tot.sale_with_vat} currency={cur} bold big />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SummarySection({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, bold, accent, big, isText, currency }) {
  const cur = currency || "TL";
  return (
    <div className={`flex justify-between items-baseline ${big ? "text-base" : "text-sm"} ${bold ? "font-semibold" : ""}`}>
      <span className="text-slate-600 dark:text-slate-400">{label}</span>
      <span className={`num ${accent ? "text-primary font-bold" : ""}`}>{isText ? value : fmt(value)} {!isText && <span className="text-[10px] text-muted-foreground ml-1">{cur}</span>}</span>
    </div>
  );
}

function RowTable({ title, cols, rows, onAdd, onUpd, onDel, totalLine, testid }) {
  return (
    <Card className="border-border">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <h4 className="font-heading font-semibold">{title}</h4>
        <div className="flex items-center gap-4">
          <div className="text-sm">{totalLine}</div>
          <Button size="sm" variant="outline" onClick={onAdd} data-testid={`${testid}-add`}><Plus size={14} className="mr-1" /> Satır Ekle</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead className="bg-slate-100 dark:bg-slate-800 text-left">
            <tr>
              {cols.map((c) => <th key={c.k} className="px-3 py-2 font-semibold uppercase tracking-widest text-[10px]">{c.label}</th>)}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={cols.length + 1} className="px-3 py-6 text-center text-muted-foreground text-xs">Henüz kalem yok.</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border">
                {cols.map((c) => (
                  <td key={c.k} className="px-2 py-1">
                    <Input
                      className="h-8"
                      type={c.type}
                      value={r[c.k] ?? ""}
                      onChange={(e) => onUpd(i, c.k, c.type === "number" ? Number(e.target.value) : e.target.value)}
                    />
                  </td>
                ))}
                <td className="px-2 py-1">
                  <Button variant="ghost" size="icon" onClick={() => onDel(i)}><Trash2 size={14} className="text-destructive" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function MaterialSalesTable({ rows, products, suppliers, onAdd, onUpd, onDel, summary }) {
  const [selectedProd, setSelectedProd] = useState("");
  return (
    <Card className="border-border">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <h4 className="font-heading font-semibold">Malzeme Alım-Satım</h4>
        <div className="text-sm">Satış: <b className="text-primary num">{fmt(summary?.subtotal_sale)}</b> / Kâr: <b className="num">{fmt(summary?.subtotal_profit)}</b></div>
        <div className="flex items-center gap-2">
          <Select value={selectedProd} onValueChange={setSelectedProd}>
            <SelectTrigger className="w-64 h-8" data-testid="product-select"><SelectValue placeholder="Kataloğdan ekle..." /></SelectTrigger>
            <SelectContent>
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" data-testid="add-material-sale-btn" onClick={() => { onAdd(products.find(x => x.id === selectedProd)); setSelectedProd(""); }}>
            <Plus size={14} className="mr-1" /> Ekle
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="material-sales-table">
          <thead className="bg-slate-100 dark:bg-slate-800 text-left">
            <tr>
              {["Kod", "Ürün", "Menşe", "Tedarikçi", "Miktar", "Alış F.", "Para", "Fire%", "Yön.", "Kâr/Marj%", "İsk%"].map(h => <th key={h} className="px-2 py-2 font-semibold uppercase tracking-widest text-[10px]">{h}</th>)}
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={12} className="px-3 py-6 text-center text-muted-foreground">Katalogdan ürün ekleyin.</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-2 py-1 font-mono">{r.product_code}</td>
                <td className="px-2 py-1">{r.product_name}</td>
                <td className="px-2 py-1"><Input className="h-7 text-xs" value={r.origin} onChange={(e) => onUpd(i, "origin", e.target.value)} /></td>
                <td className="px-2 py-1">
                  <Select value={r.supplier_name} onValueChange={(v) => onUpd(i, "supplier_name", v)}>
                    <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-1"><Input className="h-7 text-xs" type="number" value={r.quantity} onChange={(e) => onUpd(i, "quantity", Number(e.target.value))} /></td>
                <td className="px-2 py-1"><Input className="h-7 text-xs" type="number" value={r.purchase_unit_price} onChange={(e) => onUpd(i, "purchase_unit_price", Number(e.target.value))} /></td>
                <td className="px-2 py-1">
                  <Select value={r.purchase_currency} onValueChange={(v) => onUpd(i, "purchase_currency", v)}>
                    <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRY">TRY</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-1"><Input className="h-7 text-xs" type="number" value={r.waste_percent} onChange={(e) => onUpd(i, "waste_percent", Number(e.target.value))} /></td>
                <td className="px-2 py-1">
                  <Select value={r.profit_method} onValueChange={(v) => onUpd(i, "profit_method", v)}>
                    <SelectTrigger className="h-7 text-xs w-16"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">A</SelectItem>
                      <SelectItem value="B">B</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-1">
                  <Input className="h-7 text-xs" type="number"
                    value={r.profit_method === "B" ? r.target_margin_percent : r.profit_percent}
                    onChange={(e) => onUpd(i, r.profit_method === "B" ? "target_margin_percent" : "profit_percent", Number(e.target.value))} />
                </td>
                <td className="px-2 py-1"><Input className="h-7 text-xs" type="number" value={r.discount_percent} onChange={(e) => onUpd(i, "discount_percent", Number(e.target.value))} /></td>
                <td className="px-2 py-1"><Button variant="ghost" size="icon" onClick={() => onDel(i)}><Trash2 size={12} className="text-destructive" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
